import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { ChatLabels, ChatMessage, SourceReference } from "../types.js";

/** Mirrors COMPACT_MAX_WIDTH in jonas-chat-appbar.ts: the workspace's narrow layout. */
const COMPACT_MAX_WIDTH = 760;
// The identicon column of afas-message (grid-template-columns: 40px 1fr 1fr). A regular
// answer's bubble starts after it, so the panel indents by the same amount to line up
// with the answers around it. The column is gone below COMPACT_MAX_WIDTH (see
// jonas-chat-message.ts), so the indent goes with it.
const AVATAR_COLUMN_WIDTH = 40;

@customElement("help-jonas-fallback-panel")
export class HelpJonasFallbackPanel extends LitElement {
  static override styles = css`
    :host {
      display: block;
      /* Match help-jonas-chat-message rhythm so the panel doesn't sit flush
         against an adjacent afas-message / user bubble. */
      margin-bottom: 24px;
    }
    /* Same left edge and right limit as a regular answer bubble: the avatar column
       in front of it, and min(90%, 760px) for the two together. */
    .panel {
      display: grid;
      gap: 12px;
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      max-width: calc(min(90%, 760px) - ${AVATAR_COLUMN_WIDTH}px);
      margin-left: ${AVATAR_COLUMN_WIDTH}px;
      padding: 16px;
      background: var(--color-background-800);
      color: var(--color-text-on-background-800);
      border: 1px solid var(--color-foreground-300);
      /* The 4px corners of afas-message, the answer bubble and afas-alert. */
      border-radius: 4px;
      box-shadow:
        0 1px 2px rgba(15, 23, 42, 0.06),
        0 2px 8px rgba(15, 23, 42, 0.05);
    }
    .panel__search {
      display: block;
      min-width: 0;
    }
    .panel__note {
      color: var(--color-foreground-700);
      margin: 0;
    }
    .results {
      display: grid;
      gap: 4px;
    }
    /* The whole result is one clickable link, not just its title, so a click
       anywhere on the card (title, tag or excerpt) opens the article.
       user-select is disabled so dragging across it reads as "open", never as a
       stray text selection. */
    .result {
      display: grid;
      gap: 2px;
      padding: 8px 10px;
      border: 1px solid transparent;
      border-radius: 6px;
      color: inherit;
      text-decoration: none;
      cursor: pointer;
      user-select: none;
      transition:
        background-color 120ms ease,
        border-color 120ms ease;
    }
    .result:hover,
    .result:focus-visible {
      background: var(--color-background-700);
      border-color: var(--color-foreground-300);
      outline: none;
    }
    .result__link {
      color: var(--color-primary-500);
      font-weight: 600;
      text-decoration: none;
    }
    .result:hover .result__link,
    .result:focus-visible .result__link {
      text-decoration: underline;
    }
    .result__tag {
      color: var(--color-foreground-700);
    }
    .result__excerpt {
      color: var(--color-foreground-700);
    }

    /* Dark theme reads flatter, so lift the card with a stronger border and a
       deeper shadow to restore the elevation hierarchy from light mode. */
    :host-context([data-theme="dark"]) .panel,
    :host-context([theme="dark"]) .panel {
      border-color: var(--color-foreground-400);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
    }
    /* A bold, fully saturated primary link over-accentuates against the dark
       surface; a lighter weight keeps it legible without shouting. */
    :host-context([data-theme="dark"]) .result__link,
    :host-context([theme="dark"]) .result__link {
      font-weight: 500;
    }

    /* The narrow layout drops the avatar column, and the answers move left with it. */
    @media (max-width: ${COMPACT_MAX_WIDTH}px) {
      .panel {
        max-width: min(90%, 760px);
        margin-left: 0;
      }
    }

    /* On phones the panel should sit calmly inside the thread instead of
       dominating it: full width, tighter padding and spacing. */
    @media (max-width: 600px) {
      .panel {
        max-width: 100%;
        padding: 12px;
        gap: 10px;
      }
      .result {
        padding: 6px 8px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .result {
        transition: none;
      }
    }
  `;

  @property({ attribute: false })
  accessor message: ChatMessage | null = null;

  @property({ attribute: false })
  accessor labels: ChatLabels | null = null;

  // Set by the thread while the AI reconnect answer streams. It disables the
  // search (dimmed, input hidden) immediately, without freezing state — a failed
  // reconnect leaves frozen === false so the search re-enables.
  @property({ type: Boolean })
  accessor reconnecting = false;

  protected override render() {
    const message = this.message;
    if (!message?.fallback) return null;
    const frozen = message.fallback.frozen;
    const inactive = frozen || this.reconnecting;
    return html`<div class="panel">
      ${inactive
        ? this.renderRecoveredSearchBar()
        : this.renderSearchBar(message.fallback.query)}
      ${this.renderResults(message.sources)}
      ${frozen
        ? html`<p class="panel__note">
            ${this.getLabel(
              "fallbackFrozenNote",
              "AI was niet beschikbaar — Help-artikelen getoond",
            )}
          </p>`
        : nothing}
    </div>`;
  }

  private renderSearchBar(query: string) {
    // afas-search-input is the AFAS search field: it shows a visible
    // placeholder, a clear (X) button while it holds a value, and a search
    // button, and fires `search` on Enter / search-button click / clear.
    const placeholder = this.getLabel(
      "fallbackSearchPlaceholder",
      "Zoek in Help-artikelen",
    );
    return html`<afas-search-input
      class="panel__search"
      label=${placeholder}
      placeholder=${placeholder}
      .value=${query}
      @search=${this.handleRefine}
    ></afas-search-input>`;
  }

  // Once the AI Platform is back (reconnecting or frozen) the Help search is no
  // longer the way forward — the user should ask Jonas directly in the composer
  // below. The search bar stays visible but disabled, carrying that hint, instead
  // of disappearing and greying out the whole panel (which sent a contradictory
  // signal next to the still-clickable article links).
  private renderRecoveredSearchBar() {
    const hint = this.getLabel(
      "fallbackRecoveryHint",
      "Jonas (AI) weer beschikbaar — stel je vraag hieronder",
    );
    return html`<afas-search-input
      class="panel__search"
      label=${hint}
      placeholder=${hint}
      disabled
      .value=${""}
    ></afas-search-input>`;
  }

  private renderResults(sources: SourceReference[]) {
    if (!sources.length) {
      return html`<p class="panel__note">
        ${this.getLabel("fallbackEmptyState", "Geen Help-artikelen gevonden.")}
      </p>`;
    }
    return html`<div class="results">
      ${sources.map(
        (source) =>
          html`<a
            class="result"
            href=${source.url}
            @click=${(event: Event) => this.handleOpen(event, source)}
          >
            <span class="result__link"
              >${source.title || source.label || source.url}</span
            >
            ${source.sourcetag
              ? html`<span class="result__tag">${source.sourcetag}</span>`
              : nothing}
            <span class="result__excerpt">${source.excerpt}</span>
          </a>`,
      )}
    </div>`;
  }

  private handleRefine(event: Event): void {
    const detailValue = (event as CustomEvent<{ value?: string }>).detail
      ?.value;
    const targetValue = (event.currentTarget as { value?: string } | null)
      ?.value;
    const query = String(detailValue ?? targetValue ?? "").trim();
    if (!query) return;
    this.dispatchEvent(
      new CustomEvent("jonas-fallback-refine", {
        detail: { query },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleOpen(event: Event, source: SourceReference): void {
    event.preventDefault();
    this.dispatchEvent(
      new CustomEvent("jonas-source-open", {
        detail: { url: source.url, title: source.title },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private getLabel(key: string, fallback: string): string {
    return this.labels?.[key] || fallback;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-fallback-panel": HelpJonasFallbackPanel;
  }
}
